import { useEffect, useState, createElement } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Divider,
  Box,
  Image,
  SimpleGrid,
} from "@chakra-ui/react";
import { CopyIcon, DownloadIcon } from "@chakra-ui/icons";
import { FaFacebook, FaTwitter, FaInstagram } from "react-icons/fa";
import QRCode from "qrcode";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
  shortUrl?: string;
}

export const SocialShareModal = ({ isOpen, onClose, campaignId, campaignName, shortUrl }: Props) => {
  const toast = useToast();
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");

  const campaignUrl = shortUrl || `${window.location.origin}/campaign/${campaignId}`;

  useEffect(() => {
    QRCode.toDataURL(campaignUrl, {
      width: 256,
      margin: 2,
      errorCorrectionLevel: 'H'
    }).then(setQrCodeUrl);
  }, [campaignUrl]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(campaignUrl);
    toast({
      title: "Link copied to clipboard!",
      status: "success",
      duration: 2000,
    });
  };

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement("a");
    link.download = `${campaignName.replace(/\s+/g, "-")}-QR.png`;
    link.href = qrCodeUrl;
    link.click();

    toast({
      title: "QR code downloaded!",
      status: "success",
      duration: 2000,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Share Campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Campaign Link */}
            <Box>
              <Text fontWeight="600" mb={2} fontSize="sm" color="gray.600">
                Campaign Link
              </Text>
              <InputGroup size="md">
                <Input
                  value={campaignUrl}
                  readOnly
                  pr="4.5rem"
                  bg="gray.50"
                  borderColor="gray.300"
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={handleCopyLink}
                    leftIcon={<CopyIcon />}
                  >
                    Copy
                  </Button>
                </InputRightElement>
              </InputGroup>
            </Box>

            <Divider />

            {/* QR Code */}
            <VStack spacing={4}>
              <Text fontWeight="600" fontSize="sm" color="gray.600">
                QR Code
              </Text>
              <Box
                p={4}
                bg="white"
                borderRadius="md"
                boxShadow="md"
                border="1px solid"
                borderColor="gray.200"
              >
                {qrCodeUrl && (
                  <Image
                    src={qrCodeUrl}
                    alt="Campaign QR Code"
                    width="256px"
                    height="256px"
                  />
                )}
              </Box>
              <Button
                leftIcon={<DownloadIcon />}
                colorScheme="blue"
                onClick={handleDownloadQR}
                size="md"
                width="full"
                isDisabled={!qrCodeUrl}
              >
                Download QR Code
              </Button>
            </VStack>

            <Divider />

            {/* Share Options */}
            <Box>
              <Text fontWeight="600" mb={3} fontSize="sm" color="gray.600">
                Share on Social Media
              </Text>
              <SimpleGrid columns={3} spacing={3}>
                <Button
                  as="a"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(campaignUrl)}`}
                  target="_blank"
                  colorScheme="facebook"
                  leftIcon={createElement(FaFacebook as any, { size: 16 })}
                >
                  Facebook
                </Button>
                <Button
                  as="a"
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(campaignUrl)}&text=${encodeURIComponent(`Check out ${campaignName}!`)}`}
                  target="_blank"
                  colorScheme="twitter"
                  leftIcon={createElement(FaTwitter as any, { size: 16 })}
                >
                  Twitter
                </Button>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(campaignUrl);
                    toast({
                      title: "Link copied!",
                      description: "Paste it into your Instagram bio or story",
                      status: "success",
                      duration: 3000,
                    });
                  }}
                  colorScheme="pink"
                  leftIcon={createElement(FaInstagram as any, { size: 16 })}
                >
                  Instagram
                </Button>
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
